import {NotificationDetailsProps} from '@/components/Drawer/NotificationDetails'
import {EVENT_NOTIFICATION_DETAILS_QUERY_KEY} from '@/config/constants'
import {notificationTypeConfig} from '@/config/EventTriggerConfig'
import {NotificationDetailsData} from '@/types/Notifications'
import {EmailStepType} from '@/types/NotificationStep'
import {Parameters} from '@/types/Traits'
import {getNotificationByIdUseCase} from '@/useCases/getNotificationByIdUseCase'
import {useQuery} from '@tanstack/react-query'

export const useGetEventNotificationDetails = (notificationId?: string) => {
  return useQuery({
    queryKey: [EVENT_NOTIFICATION_DETAILS_QUERY_KEY, notificationId],
    queryFn: ({signal}) => getNotificationByIdUseCase({id: notificationId!}, signal),
    enabled: !!notificationId,
  })
}

// MARK: - Helpers

const mapPushStep = (responseData?: NotificationDetailsData) => {
  return {
    subject: responseData?.steps?.[0].message_configs?.[0].subject ?? '',
    message: responseData?.steps?.[0].message_configs?.[0].message ?? '',
    selectedImage: responseData?.steps?.[0].message_configs?.[0].image ?? null,
    selectedPage: responseData?.steps?.[0].message_configs?.[0].landing_page ?? null,
  }
}

const mapEmailStep = (responseData?: NotificationDetailsData): EmailStepType => {
  const step = responseData?.steps?.[0]
  const templates = step?.message_templates

  if (templates && templates.length > 0) {
    return {
      mode: 'template',
      emailSubject: templates[0]?.subject ?? '',
      selectedTemplateId: templates[0]?.id_messagetemplate?.toString() ?? '',
      name: undefined,
      html: templates[0]?.message ?? '',
    }
  }

  const config = step?.message_configs?.[0]
  return {
    mode: 'custom',
    emailSubject: config?.subject ?? '',
    emailMessage: config?.message ?? '',
    selectedImage: config?.image ?? null,
    selectedPage: config?.landing_page ?? null,
  }
}

const mapParamSelectors = (responseData?: NotificationDetailsData): Parameters => {
  return (responseData?.entry_conditions?.[0]?.traitparameters ?? []).map((item, index) => {
    return {
      id: index,
      title: `Parameter ${index + 1}`,
      trait: {
        id: item.id_traitparameter,
        name: item.trait_name,
      },
      operator: {
        id: item.id_operator,
        name: item.operator_name,
      },
      value: item.value,
      predefinedValue: {
        id: item.id_traitparametervalue ?? item.id_operatorvalue ?? item.value,
        name: item.value,
      },
    }
  }) as Parameters
}

export const notificationsDetailsPropsMapper = (responseData?: NotificationDetailsData): NotificationDetailsProps => {
  const params = responseData?.entry_conditions?.[0]
  const eventSelectors = {
    id: 0,
    title: 'Event',
    trait: {
      id: params?.trait?.id_trait ?? -1,
      name: params?.trait?.name ?? '',
    },
    operator: {
      id: params?.operator?.id_operator ?? -1,
      name: params?.operator?.name ?? '',
    },
    value: params?.value ?? '',
    predefinedValue: {
      id: params?.id_traitvalue ?? -1,
      name: params?.value ?? '',
    },
  }
  return {
    notificationName: responseData?.name ?? '',
    notificationDescription: responseData?.description ?? '',
    country: responseData?.country_name ?? '',
    event: responseData?.eventStatus?.name ?? '',
    eventStatus: responseData?.eventStatus,
    eventSelectors: eventSelectors,
    paramSelectors: mapParamSelectors(responseData),
    period: responseData?.period,
    timeExecutionWindow: responseData?.timeExecutionWindow,
    fallsOnBankHoliday: responseData?.fallsOnBankHoliday,
    notificationType: notificationTypeConfig.find(
      item => item.id === responseData?.steps?.[0]?.stepType?.id_steptype?.toString()
    ),
    pushStep: mapPushStep(responseData),
    smsStep: {message: 'Placeholder for SMS Message'},
    whatsAppStep: {
      selectedTemplateId: responseData?.steps?.[0]?.message_templates?.[0]?.id_messagetemplate?.toString() ?? '',
      whatsAppMessage: responseData?.steps?.[0]?.message_templates?.[0]?.message ?? '',
    },
    emailStep: mapEmailStep(responseData),
  }
}
